import type { AudioCue } from "./audio";

/**
 * Which cues the hand feels (S5): the platform's haptics (infra/platform/haptics.ts) follow the sound port, so a build,
 * a tier-up, a hit on the fortress and the end of a level are felt wherever they are heard, with no second set of
 * hooks in the level code. Kinds name what happened: `tap` answers the player's own build or trade, `success` a reward,
 * `warning` the fortress taking a hit or the level lost, `heavy` a boss arriving or changing phase. Shots, hits and
 * deaths are never felt (dozens a second), nor is the menu click.
 */
export type HapticFeel = "tap" | "success" | "warning" | "heavy";

export const CUE_HAPTICS: Partial<Record<AudioCue, HapticFeel>> = {
  towerPlace: "tap", roadPlace: "tap", hubUpgradeStart: "tap", queueSet: "tap", marketBuy: "tap", marketSell: "tap",
  towerUpgrade: "success", towerUpgradeMax: "success", hubUpgradeDone: "success", chest: "success", crateOpen: "success",
  purchase: "success", achievement: "success", win: "success",
  leak: "warning", lose: "warning",
  boss: "heavy", bossPhase: "heavy",
  rareSpawn: "heavy", rareKill: "success",
  // the chest stage: the landing and the burst are felt hard, each tap and the lock as a tap, a climb and a great card as a reward
  chestLand: "heavy", chestBurst: "heavy", chestRattle: "tap", chestLockBreak: "tap", cardFlip: "tap",
  chestTierUp: "success", rarityEpic: "success", rarityLegendary: "success", rarityMythic: "heavy",
  // M40, the Starfall Wyrm: its impacts and roars felt hard, the escape as a warning, the skin as a reward; its herald
  // (under the dive), wingbeats, glitter, hits and the choice's sting are not felt
  wyrm_dive: "heavy", wyrm_roar: "heavy", wyrm_return: "heavy", wyrm_death: "heavy", wyrm_crash: "heavy",
  wyrm_flee: "warning", wyrm_unlock: "success",
};

/**
 * The shortest time between two haptics of one kind (s): a leak streak or a road dragged cell by cell is felt as a
 * pulse, not a buzz. Each kind has its own clock, so a win is never swallowed by the leak just before it.
 */
export const HAPTIC_GAP_SECONDS: Readonly<Record<HapticFeel, number>> = { tap: 0.06, success: 0.4, warning: 0.5, heavy: 0.8 };

/** The haptic a cue asks for, or null for a cue the hand does not feel. */
export function hapticFor(cue: AudioCue): HapticFeel | null { return CUE_HAPTICS[cue] ?? null; }

/** Rate-limits haptics per kind (`now` in seconds, any monotonic clock). */
export class HapticGate {
  private readonly last = new Map<HapticFeel, number>();
  admit(kind: HapticFeel, now: number): boolean {
    const prev = this.last.get(kind);
    if (prev !== undefined && now - prev < HAPTIC_GAP_SECONDS[kind]) return false;
    this.last.set(kind, now);
    return true;
  }
}
